import type { ReactNode } from 'react'
import type { StagedActionViewContentMeta } from '@renderer/overlay/Sidepanel/hooks/use-staged-actions'
import { resolveContactAvatarSrc } from '@renderer/utils/contact-avatar'
import { twMerge } from 'tailwind-merge'

interface CommenterMetaProps {
  contentMeta?: StagedActionViewContentMeta
  /** The attributed content, rendered to the right of the avatar, below the name. */
  children?: ReactNode
  className?: string
  /** Applied to the content only when there is no commenter header (e.g. spacing from preceding rows). */
  contentClassName?: string
}

// Larger avatar in a left column, with the commenter name on its own line above
// the content. Rendered in both the full action view and the cell preview, so it
// can't live inside ContentFrame (which only mounts in the full view). With no
// commenter it renders the content untouched (apart from `contentClassName`).
export function CommenterMeta({ contentMeta, children, className, contentClassName }: CommenterMetaProps) {
  const name = contentMeta?.commenterName
  const icon = resolveContactAvatarSrc(contentMeta?.commenterIcon)
  if (!name && !icon) {
    return contentClassName ? <div className={contentClassName}>{children}</div> : <>{children}</>
  }

  return (
    <div className={twMerge('flex items-start gap-2.5', className)}>
      {icon && <img src={icon} alt="" className="size-8 shrink-0 rounded-full" />}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {name && <span className="text-foreground text-sm font-semibold leading-none">{name}</span>}
        {children}
      </div>
    </div>
  )
}

export function hasCommenterMeta(contentMeta?: StagedActionViewContentMeta): boolean {
  return !!contentMeta?.commenterName || !!contentMeta?.commenterIcon
}
